import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './AnalyticsAdminPage.css'

const TOKEN_STORAGE = 'st_admin_dashboard_token'
const DAY_OPTIONS = [7, 14, 30, 90]

function loadToken() {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE) || ''
  } catch {
    return ''
  }
}

function saveToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_STORAGE, token)
    else sessionStorage.removeItem(TOKEN_STORAGE)
  } catch {
    // ignore
  }
}

function downloadCsv(filename, rows) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const csv = rows.map((r) => r.map(escape).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function formatDay(day) {
  if (!day) return '—'
  // day may be "2026-08-12" or ISO
  return String(day).slice(0, 10)
}

function BarChart({ data, valueKey = 'pageviews', labelKey = 'day' }) {
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey]) || 0))
  if (!data.length) {
    return <p className="admin-muted">Ingen data i perioden.</p>
  }
  return (
    <div className="admin-bars" role="img" aria-label="Søjlediagram">
      {data.map((row) => {
        const value = Number(row[valueKey]) || 0
        const pct = Math.round((value / max) * 100)
        return (
          <div className="admin-bar-row" key={`${row[labelKey]}-${value}`}>
            <div className="admin-bar-label">{formatDay(row[labelKey])}</div>
            <div className="admin-bar-track">
              <div className="admin-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="admin-bar-val">{value}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function AnalyticsAdminPage() {
  const [token, setToken] = useState(loadToken)
  const [username, setUsername] = useState('')
  const [code, setCode] = useState('')
  const [days, setDays] = useState(14)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [purgeDays, setPurgeDays] = useState(90)
  const [actionMsg, setActionMsg] = useState('')

  const authed = Boolean(token)

  const authHeaders = useCallback(
    (extra = {}) => ({
      ...extra,
      Authorization: `Bearer ${token}`,
      'X-Dashboard-Token': token,
    }),
    [token],
  )

  const fetchSummary = useCallback(
    async (authToken, period) => {
      setLoading(true)
      setError('')
      setActionMsg('')
      try {
        const res = await fetch(`/api/analytics/summary?days=${period}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'X-Dashboard-Token': authToken,
          },
        })
        if (res.status === 401 || res.status === 503) {
          throw new Error('Session udløbet eller ugyldig login — log ind igen')
        }
        if (!res.ok) throw new Error(`Kunne ikke hente data (${res.status})`)
        const json = await res.json()
        setData(json)
      } catch (e) {
        setData(null)
        setError(e.message || 'Fejl')
        if (String(e.message || '').includes('Session')) {
          saveToken('')
          setToken('')
        }
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!token) return
    fetchSummary(token, days)
  }, [token, days, fetchSummary])

  const onLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/analytics/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          code: code,
        }),
      })
      if (res.status === 401) throw new Error('Forkert brugernavn eller kode')
      if (res.status === 503) throw new Error('Dashboard-login er ikke konfigureret på serveren')
      if (!res.ok) throw new Error(`Login fejlede (${res.status})`)
      const json = await res.json()
      saveToken(json.token)
      setToken(json.token)
      setCode('')
    } catch (err) {
      setError(err.message || 'Login fejlede')
    } finally {
      setLoading(false)
    }
  }

  const onLogout = () => {
    saveToken('')
    setToken('')
    setUsername('')
    setCode('')
    setData(null)
    setError('')
  }

  const onExport = () => {
    if (!data) return
    const rows = [
      ['section', 'key', 'pageviews', 'visitors'],
      ...data.daily.map((d) => ['daily', d.day, d.pageviews, d.visitors]),
      ...data.top_paths.map((p) => ['path', p.path, p.pageviews, p.visitors]),
      ...data.top_referrers.map((r) => ['referrer', r.host, r.pageviews, '']),
    ]
    downloadCsv(`statistik-${days}d.csv`, rows)
    setActionMsg('CSV downloadet.')
  }

  const onPurge = async () => {
    if (!token) return
    const ok = window.confirm(
      `Slet alle sidevisninger ældre end ${purgeDays} dage?\nDet kan ikke fortrydes.`,
    )
    if (!ok) return
    setActionMsg('')
    try {
      const res = await fetch('/api/analytics/purge', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ older_than_days: purgeDays }),
      })
      if (!res.ok) throw new Error(`Purge fejlede (${res.status})`)
      const json = await res.json()
      setActionMsg(`Slettet ${json.deleted} rækker ældre end ${json.older_than_days} dage.`)
      fetchSummary(token, days)
    } catch (e) {
      setError(e.message || 'Purge fejlede')
    }
  }

  const maxDaily = useMemo(
    () => Math.max(1, ...(data?.daily || []).map((d) => d.pageviews || 0)),
    [data],
  )

  return (
    <div className="admin-analytics">
      <header className="admin-analytics__header">
        <div>
          <p className="admin-eyebrow">Admin</p>
          <h1>Statistik</h1>
          <p className="admin-muted">
            Consent-baserede sidevisninger ·{' '}
            <Link to="/privatliv">privatliv</Link>
            {' · '}
            <Link to="/">til sitet</Link>
          </p>
        </div>
        {authed && (
          <button type="button" className="admin-btn admin-btn--ghost" onClick={onLogout}>
            Log ud
          </button>
        )}
      </header>

      {!authed ? (
        <form className="admin-login" onSubmit={onLogin}>
          <label htmlFor="admin-user">Brugernavn</label>
          <input
            id="admin-user"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <label htmlFor="admin-code">Kode</label>
          <input
            id="admin-code"
            type="password"
            autoComplete="current-password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          {error && <div className="admin-alert admin-alert--error">{error}</div>}
          <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
            {loading ? 'Logger ind…' : 'Log ind'}
          </button>
          <p className="admin-muted">
            Sessionen gælder ca. 12 timer og gemmes kun i denne fane (sessionStorage).
          </p>
        </form>
      ) : (
        <>
          <div className="admin-toolbar">
            <div className="admin-days" role="group" aria-label="Periode">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`admin-chip ${days === d ? 'is-active' : ''}`}
                  onClick={() => setDays(d)}
                >
                  {d} dage
                </button>
              ))}
            </div>
            <div className="admin-toolbar__actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={() => fetchSummary(token, days)}
                disabled={loading}
              >
                Opdater
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={onExport}
                disabled={!data}
              >
                Eksportér CSV
              </button>
            </div>
          </div>

          {error && <div className="admin-alert admin-alert--error">{error}</div>}
          {actionMsg && <div className="admin-alert admin-alert--ok">{actionMsg}</div>}
          {loading && !data && <p className="admin-muted">Henter…</p>}

          {data && (
            <>
              <section className="admin-stats">
                <div className="admin-stat">
                  <div className="admin-stat__v">{data.total_pageviews}</div>
                  <div className="admin-stat__l">Sidevisninger ({days}d)</div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat__v">{data.unique_visitors}</div>
                  <div className="admin-stat__l">Unikke besøgende</div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat__v">{data.returning_visitors}</div>
                  <div className="admin-stat__l">Returning</div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat__v">{data.pages_per_visitor ?? '—'}</div>
                  <div className="admin-stat__l">Sider / besøgende</div>
                </div>
                <div className="admin-stat">
                  <div className="admin-stat__v">{data.total_pageviews_all_time}</div>
                  <div className="admin-stat__l">I alt (alle tider)</div>
                </div>
              </section>

              <div className="admin-grid">
                <section className="admin-card">
                  <h2>Sidevisninger pr. dag</h2>
                  <BarChart data={data.daily} />
                  <p className="admin-muted admin-tiny">
                    Max/dag i perioden: {maxDaily}
                    {data.newest_event ? ` · seneste event ${formatDay(data.newest_event)}` : ''}
                  </p>
                </section>

                <section className="admin-card">
                  <h2>Top sider</h2>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Sti</th>
                        <th>Views</th>
                        <th>Unikke</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_paths.map((row) => (
                        <tr key={row.path}>
                          <td>
                            <code>{row.path}</code>
                          </td>
                          <td>{row.pageviews}</td>
                          <td>{row.visitors}</td>
                          <td>
                            <a href={row.path} target="_blank" rel="noreferrer">
                              Åbn
                            </a>
                          </td>
                        </tr>
                      ))}
                      {!data.top_paths.length && (
                        <tr>
                          <td colSpan={4} className="admin-muted">
                            Ingen sider endnu — kræver accept af statistik-cookies.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>

                <section className="admin-card">
                  <h2>Top referrers</h2>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Domæne</th>
                        <th>Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_referrers.map((row) => (
                        <tr key={row.host}>
                          <td>{row.host}</td>
                          <td>{row.pageviews}</td>
                        </tr>
                      ))}
                      {!data.top_referrers.length && (
                        <tr>
                          <td colSpan={2} className="admin-muted">
                            Ingen eksterne referrers i perioden.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>

                <section className="admin-card">
                  <h2>Seneste events</h2>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Tid</th>
                        <th>Sti</th>
                        <th>Referrer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recent || []).map((row) => (
                        <tr key={row.id}>
                          <td>{row.created_at?.replace('T', ' ').slice(0, 19)}</td>
                          <td>
                            <code>{row.path}</code>
                          </td>
                          <td>{row.referrer_host || '—'}</td>
                        </tr>
                      ))}
                      {!(data.recent || []).length && (
                        <tr>
                          <td colSpan={3} className="admin-muted">
                            Ingen events.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>
              </div>

              <section className="admin-card admin-danger-zone">
                <h2>Vedligeholdelse</h2>
                <p className="admin-muted">
                  Slet gamle rå events for at holde databasen slank. Minimum 7 dage beholdes.
                  {data.oldest_event
                    ? ` Ældste event lige nu: ${formatDay(data.oldest_event)}.`
                    : ''}
                </p>
                <div className="admin-purge">
                  <label>
                    Ældre end
                    <select
                      value={purgeDays}
                      onChange={(e) => setPurgeDays(Number(e.target.value))}
                    >
                      <option value={30}>30 dage</option>
                      <option value={90}>90 dage</option>
                      <option value={180}>180 dage</option>
                      <option value={365}>365 dage</option>
                    </select>
                  </label>
                  <button type="button" className="admin-btn admin-btn--danger" onClick={onPurge}>
                    Slet gamle events
                  </button>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  )
}
